import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from './api';
import type { Application, Superuser, User } from './api';

const STORAGE_KEY = 'ihm-auth-session';

export type Session =
  | { kind: 'superuser'; token: string; superuser: Superuser }
  | {
      kind: 'user';
      token: string;
      user: User;
      application: { applicationId: string; name: string } | null;
      permissions: string[];
    };

type AuthValue = {
  session: Session | null;
  /** True until the stored session has been revalidated against the API. */
  loading: boolean;
  loginSuperuser: (email: string, password: string) => Promise<void>;
  loginUser: (applicationId: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  refresh: () => Promise<void>;
  /** Master users hold `*`, so this answers true for any code. */
  can: (code: string) => boolean;
};

const AuthContext = createContext<AuthValue | null>(null);

function readStored(): Session | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(readStored);
  const [loading, setLoading] = useState(true);

  const persist = useCallback((next: Session | null) => {
    setSession(next);
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  /**
   * Re-fetches the session from the API. A stored token may have expired or
   * had its account deleted while the tab was closed, and permissions can
   * change server-side, so the local copy is never trusted on its own.
   */
  const refresh = useCallback(async () => {
    const stored = readStored();
    if (!stored) {
      setLoading(false);
      return;
    }
    try {
      if (stored.kind === 'superuser') {
        const { superuser } = await api.superuserMe(stored.token);
        persist({ kind: 'superuser', token: stored.token, superuser });
      } else {
        const me = await api.me(stored.token);
        persist({
          kind: 'user',
          token: stored.token,
          user: me.user,
          application: me.application,
          permissions: me.permissions,
        });
      }
    } catch (err) {
      // Only drop the session when the server actually rejected it; a network
      // blip should not sign the user out.
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) persist(null);
    } finally {
      setLoading(false);
    }
  }, [persist]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const loginSuperuser = useCallback(
    async (email: string, password: string) => {
      const res = await api.superuserLogin(email, password);
      persist({ kind: 'superuser', token: res.token, superuser: res.superuser });
    },
    [persist],
  );

  const loginUser = useCallback(
    async (applicationId: string, email: string, password: string) => {
      const res = await api.login(applicationId, email, password);
      const me = await api.me(res.token);
      persist({
        kind: 'user',
        token: res.token,
        user: me.user,
        application: me.application,
        permissions: me.permissions,
      });
    },
    [persist],
  );

  const logout = useCallback(() => persist(null), [persist]);

  const can = useCallback(
    (code: string) => {
      if (!session || session.kind !== 'user') return false;
      return session.permissions.includes('*') || session.permissions.includes(code);
    },
    [session],
  );

  const value = useMemo(
    () => ({ session, loading, loginSuperuser, loginUser, logout, refresh, can }),
    [session, loading, loginSuperuser, loginUser, logout, refresh, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** Convenience for pages that only render inside a tenant session. */
export function useTenantSession() {
  const { session } = useAuth();
  if (!session || session.kind !== 'user') {
    throw new Error('This view requires an application user session');
  }
  return session;
}

/** Convenience for pages that only render inside a superuser session. */
export function useSuperuserSession() {
  const { session } = useAuth();
  if (!session || session.kind !== 'superuser') {
    throw new Error('This view requires a superuser session');
  }
  return session;
}

export type { Application };
